/**
 * Horizontal scroll row for Kanban columns.
 *
 * Two modes:
 * - `withoutDndContext` (used by Kanban organism): parent owns DndContext so
 *   swimlanes can share one drag session; this component only scrolls + optional
 *   horizontal SortableContext for column reorder.
 * - Standalone (default): wraps children in its own DndContext + DragOverlay for
 *   custom boards that compose primitives without the full Kanban organism.
 */

import type { ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  defaultDropAnimationSideEffects,
  type DropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/utils/cn';
import {
  kanbanBoardRowClass,
  kanbanBoardRowGapClass,
  kanbanBoardScrollClass,
} from './Kanban.styles';
import type { KanbanBoardProps } from './Kanban.types';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

interface KanbanBoardExtendedProps extends KanbanBoardProps {
  /**
   * When true, render only the scroll row (parent owns DndContext).
   * Required for multi-lane boards that share one drag context.
   */
  withoutDndContext?: boolean;
  /** Extra content rendered inside DndContext after the row (e.g. lanes). */
  afterRow?: ReactNode;
}

export function KanbanBoard({
  children,
  sensors,
  collisionDetection = closestCorners,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragCancel,
  overlay,
  columnIds,
  className,
  rowClassName,
  withoutDndContext = false,
  afterRow,
}: KanbanBoardExtendedProps) {
  const columnsRow = (
    <div
      className={cn(
        kanbanBoardRowClass,
        rowClassName == null && kanbanBoardRowGapClass,
        rowClassName
      )}
    >
      {children}
    </div>
  );

  const sortableRow =
    columnIds && columnIds.length > 0 ? (
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        {columnsRow}
      </SortableContext>
    ) : (
      columnsRow
    );

  if (withoutDndContext) {
    return (
      <div className={cn(kanbanBoardScrollClass, className)}>
        {sortableRow}
        {afterRow}
      </div>
    );
  }

  return (
    <div className={cn(kanbanBoardScrollClass, className)}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {sortableRow}
        {afterRow}
        <DragOverlay dropAnimation={dropAnimation}>{overlay ?? null}</DragOverlay>
      </DndContext>
    </div>
  );
}
